'use client';

import styles from '@/app/(consent-manger)/[locale]/(popover-footer)/(custome-header-design)/layout.module.css'; // Adjust path if needed
import { ThemedUserButton } from '@/components/auth/ThemedUserButton';
import { SidebarMenuContent } from '@/components/layout/SidebarMenuContent';
import CrownButton from '@/components/marketing/CrownButton';
import { TrialBanner } from '@/components/pricing/TrialBanner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AppSidebar } from '@/elements/research/components/AppSidebar';
import { SidebarActionList } from '@/elements/research/components/SidebarActionList';
import { useAuth } from '@/hooks/useAuth';
import { useRouteKind } from '@/hooks/useRouteKind';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { selectCustomizeSheetOpen } from '@/lib/redux/selectors/uiSettingsSelectors';
import { setCustomizeSheetOpen } from '@/lib/redux/slices/uiSettingsSlice';
import { selectUsageData } from '@/lib/redux/slices/usageSlice';
import { cn } from '@/lib/utils';
import { MOBILE_VIEW_BREAKPOINT_PX } from '@/utils/breakpoints';
import {
  SIDEBAR_EXPANDED_WIDTH_ANON,
  SIDEBAR_EXPANDED_WIDTH_AUTH,
} from '@/utils/sidebarLayout';
import { Menu, Search, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import React, { useEffect, useRef, useState } from 'react';

// SearchHistoryDialog dynamic-imported because (a) it only renders when the
// sidebar is collapsed + the user opens it via the search icon, and (b) it
// transitively pulls in ~5 framer-motion-using history components
// (ChatHistoryTab, SearchHistoryTab, ResearchHistoryTab, HistoryTabs,
// HistoryItem). Deferring keeps framer-motion out of the chat-page critical
// path entirely (#4053).
const SearchHistoryDialog = dynamic(
  () =>
    import('@/elements/research/components/SearchHistoryDialog').then((m) => ({
      default: m.SearchHistoryDialog,
    })),
  { ssr: false }
);
// CustomizeSheet rendered here (not inside SidebarActionList) so the open state
// survives the sidebar list unmounting on mobile drawer collapse (#5238). It's
// the single owner of the sheet across every SidebarActionList instance
// (expanded sidebar, collapsed rail, mobile drawer). Dynamic-imported because it
// transitively pulls in UserTypePopup + PreferencesPage + AppearanceSettings and
// only matters once the user actually opens Customize.
const CustomizeSheet = dynamic(
  () =>
    import('@/elements/research/components/CustomizeSheet').then((m) => ({
      default: m.CustomizeSheet,
    })),
  { ssr: false }
);

interface MainContentAreaProps {
  children: React.ReactNode;
}

export default function MainContentArea({ children }: MainContentAreaProps) {
  const { isAuthenticated, isLoading } = useAuth();
  // Typed pathname→route-flag derivation (single source of truth, #5814). The
  // full-bleed exclusion (rendersOwnFullBleedShell) is the load-bearing #5158/
  // #5160 invariant: pages that render their own width:100% shell must never be
  // horizontally centered by <main> — see useRouteKind for the full rationale.
  const {
    isSharePage,
    isDocumentPage,
    isAnonSidebarPage,
    rendersOwnFullBleedShell,
  } = useRouteKind();

  // Determine if we should show the sidebar
  // - For authenticated users: show on all pages except share pages
  // - For anonymous users: show on search and research pages (simplified version)
  const isAnonymousWithSidebar =
    !isLoading && !isAuthenticated && isAnonSidebarPage;
  const shouldShowSidebar =
    !isLoading && !isSharePage && (isAuthenticated || isAnonymousWithSidebar);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] =
    useState<boolean>(false);
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState<boolean>(false);
  const dragStartXRef = useRef<number>(0);

  const tSidebarHeader = useTranslations('SidebarHeader');
  const tCommon = useTranslations('Common');
  const tMarketing = useTranslations('marketing');

  // Check if user has a paid plan via zadanie data (replaces old Stripe check).
  // Gates the crown upsell on the typed `is_paid_plan` flag, never the display
  // name — see ZadaniePlanInfo.is_paid_plan.
  const usageData = useAppSelector(selectUsageData);
  const zadanie = usageData?.zadanie ?? null;
  const isPremiumActive = zadanie?.plan.is_paid_plan === true;

  // In-shell Customize sheet open state (#5238) — owned here so it survives the
  // sidebar list unmounting on mobile drawer collapse. The sidebar Customize
  // buttons dispatch setCustomizeSheetOpen(true); this component renders the one
  // <CustomizeSheet> instance.
  const dispatch = useAppDispatch();
  const customizeSheetOpen = useAppSelector(selectCustomizeSheetOpen);

  // Load persisted state or default to collapsed for anonymous users
  useEffect(() => {
    // For anonymous users, default to collapsed sidebar
    if (isAnonymousWithSidebar) {
      setIsSidebarCollapsed(true);
      return;
    }

    // For authenticated users, load from localStorage
    try {
      const persisted = localStorage.getItem('researchSidebarCollapsed');
      if (persisted != null) {
        setIsSidebarCollapsed(persisted === 'true');
      }
    } catch {
      // noop – localStorage might be blocked
    }
  }, [isAnonymousWithSidebar]);

  // Listen for external open requests (from header banner on mobile)
  useEffect(() => {
    const openHandler = () => setIsMobileSidebarOpen(true);
    window.addEventListener('sidebar:open', openHandler as EventListener);
    return () => {
      window.removeEventListener('sidebar:open', openHandler as EventListener);
    };
  }, []);

  // Listen for search event when sidebar is collapsed (AppSidebar not mounted)
  useEffect(() => {
    if (!isSidebarCollapsed) return;

    const searchHandler = () => {
      setIsSearchDialogOpen(true);
    };
    window.addEventListener('sidebar:search', searchHandler as EventListener);
    return () => {
      window.removeEventListener(
        'sidebar:search',
        searchHandler as EventListener
      );
    };
  }, [isSidebarCollapsed]);

  // Global ⌘K / Ctrl+K opens the search palette from anywhere in the app shell
  // (claude.com pattern). It dispatches the same `sidebar:search` event the rail
  // buttons use, so whichever surface is mounted (the collapsed-rail dialog here
  // or AppSidebar's when expanded) handles it — no duplicate dialog state.
  useEffect(() => {
    if (!shouldShowSidebar) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('sidebar:search'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shouldShowSidebar]);

  // Track if viewport is mobile (< mobile-view) to conditionally mount mobile sidebar
  useEffect(() => {
    const handleResize = () =>
      setIsMobileScreen(window.innerWidth < MOBILE_VIEW_BREAKPOINT_PX);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync CSS variables for header/banner positioning and crown centering.
  // Single-column sidebar (claude.com pattern): expanded it is one fixed-width
  // column; collapsed it is the 56px icon rail.
  const expandedSidebarWidth = isAnonymousWithSidebar
    ? SIDEBAR_EXPANDED_WIDTH_ANON
    : SIDEBAR_EXPANDED_WIDTH_AUTH;

  useEffect(() => {
    const offset =
      !shouldShowSidebar || isMobileScreen
        ? '0px'
        : isSidebarCollapsed
          ? '56px'
          : expandedSidebarWidth;

    const sidebarWidth =
      !shouldShowSidebar || isMobileScreen
        ? '0px'
        : isSidebarCollapsed
          ? '56px'
          : expandedSidebarWidth;

    try {
      document.documentElement.style.setProperty(
        '--sidebar-left-offset',
        offset
      );
      document.documentElement.style.setProperty('--sidebar-w', sidebarWidth);
    } catch {
      // noop
    }
  }, [
    shouldShowSidebar,
    isSidebarCollapsed,
    isMobileScreen,
    expandedSidebarWidth,
  ]);

  // Persist state (only when not on document page to preserve user preference)
  useEffect(() => {
    if (!isDocumentPage) {
      try {
        localStorage.setItem(
          'researchSidebarCollapsed',
          String(isSidebarCollapsed)
        );
      } catch {
        // noop
      }
    }
  }, [isSidebarCollapsed, isDocumentPage]);

  // Close sidebar when navigating to document page
  useEffect(() => {
    if (isDocumentPage) {
      setIsSidebarCollapsed(true);
      setIsMobileSidebarOpen(false);
    }
  }, [isDocumentPage]);

  // Drag handlers for expand (when collapsed)
  const handleDragStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
  }, []);

  const handleDragMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartXRef.current;
      const EXPAND_THRESHOLD = 50; // Drag right 50px to expand

      if (deltaX > EXPAND_THRESHOLD) {
        // Dragged right enough, expand the sidebar
        setIsDragging(false);
        setIsSidebarCollapsed(false);
      }
    },
    [isDragging]
  );

  const handleDragEnd = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  // Attach/detach mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
    return; // Explicit return for all code paths
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <div className={cn(styles['appShell'], 'mx-auto w-full')}>
      {/* Search dialog when sidebar is collapsed */}
      {isSidebarCollapsed && (
        <SearchHistoryDialog
          isOpen={isSearchDialogOpen}
          onClose={() => setIsSearchDialogOpen(false)}
        />
      )}
      {/* Single in-shell Customize sheet (#5238). Mounted at the shell root and
          gated only on the sidebar being shown — NOT on collapse/drawer state —
          so opening Customize survives the sidebar list unmounting on mobile
          drawer collapse. Closed by default; its heavy children only mount on
          open. */}
      {shouldShowSidebar && (
        <CustomizeSheet
          open={customizeSheetOpen}
          onOpenChange={(open) => dispatch(setCustomizeSheetOpen(open))}
        />
      )}
      <div
        className={cn(
          styles['contentWrapper'],
          // Use full width layout when: sidebar not shown OR sidebar collapsed
          (!shouldShowSidebar || isSidebarCollapsed) &&
            styles['contentWrapperFullWidth'],
          'mobile-view:max-w-full mobile-view:overflow-x-hidden'
        )}
        style={{
          position: 'relative',
          // Sidebar width only matters when expanded (narrower for anonymous users)
          ['--sidebar-w' as any]: isSidebarCollapsed
            ? '0px'
            : expandedSidebarWidth,
        }}
      >
        {/* Mobile sidebar toggle button (top-left) */}
        {shouldShowSidebar && !isMobileSidebarOpen && (
          <button
            type='button'
            aria-label={tCommon('openSidebar')}
            title={tCommon('openSidebar')}
            className={cn(
              'mobile-view:hidden fixed top-3 left-3 z-50 flex items-center justify-center min-h-[44px] min-w-[44px] p-2 rounded-md',
              'bg-transparent text-content-secondary',
              'hover:bg-surface-subtle/60'
            )}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileSidebarOpen(true);
            }}
          >
            <Menu size={18} />
          </button>
        )}

        {/* Mobile off-canvas sidebar with smooth transitions */}
        {shouldShowSidebar && isMobileScreen && (
          <div
            className={cn(
              'mobile-view:hidden fixed inset-0 z-[1100] flex',
              'transition-opacity duration-300 ease-in-out',
              isMobileSidebarOpen
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            )}
          >
            <div
              className={cn(
                // Single-column drawer width.
                'h-full w-[88%] max-w-[340px] bg-surface-base shadow-xl',
                'transform transition-transform duration-300 ease-in-out',
                isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <AppSidebar
                onRequestCollapse={() => {
                  setIsMobileSidebarOpen(false);
                }}
                isAnonymousMode={isAnonymousWithSidebar}
              />
            </div>
            <button
              aria-label={tCommon('closeSidebar')}
              title={tCommon('closeSidebar')}
              className={cn(
                'flex-1 h-full',
                'transition-colors duration-300 ease-in-out',
                isMobileSidebarOpen ? 'bg-black/30' : 'bg-black/0'
              )}
              onClick={() => {
                setIsMobileSidebarOpen(false);
              }}
            />
          </div>
        )}

        {/* Sidebar grid cell - only render when expanded (like ReferenceSidebar) */}
        {shouldShowSidebar && !isSidebarCollapsed && (
          <div className={cn(styles['sidebar'], 'hidden mobile-view:block')}>
            <AppSidebar
              onRequestCollapse={() => setIsSidebarCollapsed(true)}
              isAnonymousMode={isAnonymousWithSidebar}
            />
          </div>
        )}
        {/* Sidebar toggle button (desktop only) */}
        {shouldShowSidebar && isSidebarCollapsed && (
          <div
            className={cn(
              'hidden mobile-view:flex flex-col items-stretch group',
              'absolute inset-y-0 left-0 w-14 z-40',
              'bg-surface-sidebar shadow-sm',
              'overflow-hidden'
            )}
            style={{ cursor: 'ew-resize' }}
            onClick={() => setIsSidebarCollapsed(false)}
            onWheel={(e) => {
              e.preventDefault();
              const chatContainer = document.getElementById(
                'chat-scroll-container'
              );
              if (chatContainer) {
                const newScrollTop = chatContainer.scrollTop + e.deltaY;
                const maxScrollTop =
                  chatContainer.scrollHeight - chatContainer.clientHeight;
                chatContainer.scrollTop = Math.max(
                  0,
                  Math.min(newScrollTop, maxScrollTop)
                );
              }
            }}
          >
            <div
              className='absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-brand-tertiary transition-colors z-20'
              aria-hidden='true'
              onMouseDown={handleDragStart}
              onDoubleClick={() => setIsSidebarCollapsed(false)}
            >
              <div className='absolute right-0 top-0 bottom-0 w-3 -mr-1' />
            </div>
            <div className='flex-1 flex flex-col h-full justify-between'>
              <div>
                <SidebarActionList
                  onNewChatClick={() => {
                    window.dispatchEvent(new CustomEvent('sidebar:newChat'));
                  }}
                  onToggleCollapse={() => setIsSidebarCollapsed(false)}
                  showRightIcon={false}
                  showOpenButton={false}
                  // Drop auth-only sections (Inbox) for anonymous users — same
                  // gate the expanded SidebarRail applies (#6355).
                  isAnonymousMode={isAnonymousWithSidebar}
                  // Clicking a section in the collapsed rail expands the sidebar
                  // (the rail's parent onClick) and navigates to that section.
                />
                {/* Search chats — separated from nav actions */}
                {!isAnonymousWithSidebar && (
                  <div className='pt-1.5 mt-1.5 mx-1.5 border-t border-line-subtle'>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={tSidebarHeader('searchChats')}
                            aria-haspopup='dialog'
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsSidebarCollapsed(false);
                              requestAnimationFrame(() => {
                                window.dispatchEvent(
                                  new CustomEvent('sidebar:search')
                                );
                              });
                            }}
                            className='group w-full flex items-center justify-center text-left rounded-md min-h-[44px] min-w-[44px] px-1.5 py-1.5 text-sm hover:bg-interactive-hover transition-colors cursor-pointer'
                          >
                            <Search
                              size={20}
                              className='text-brand-primary-color'
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side='right'
                          align='center'
                          sideOffset={8}
                        >
                          <span>{tSidebarHeader('searchChats')}</span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
              <div
                className='flex flex-col items-center gap-1 pb-2'
                onClick={(e) => e.stopPropagation()}
              >
                <TooltipProvider delayDuration={0}>
                  {/* Account button - only for authenticated users */}
                  {!isAnonymousWithSidebar && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <ThemedUserButton userProfileUrl='/user-profile'>
                            <button
                              aria-label={tSidebarHeader('account')}
                              className='group flex h-11 w-11 items-center justify-center rounded-md p-2 hover:bg-surface-muted transition-colors cursor-pointer'
                            >
                              <User
                                size={20}
                                className='text-brand-primary-color'
                              />
                            </button>
                          </ThemedUserButton>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side='right'
                        align='center'
                        sideOffset={8}
                      >
                        <span>{tSidebarHeader('account')}</span>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Popover>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button
                            aria-label={tCommon('openFooterMenu')}
                            className='group flex h-11 w-11 items-center justify-center rounded-md p-2 hover:bg-surface-muted transition-colors cursor-pointer'
                          >
                            <Menu
                              size={20}
                              className='text-brand-primary-color'
                            />
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent
                        side='right'
                        align='center'
                        sideOffset={8}
                      >
                        <span>{tCommon('menu')}</span>
                      </TooltipContent>
                    </Tooltip>
                    <PopoverContent
                      className='w-max p-2'
                      align='start'
                      side='right'
                    >
                      <SidebarMenuContent
                        showCookiePreferences={isAuthenticated}
                        showCustomize
                      />
                    </PopoverContent>
                  </Popover>
                  {/* Customize lives in the shared footer menu
                      (SidebarMenuContent) — no separate rail icon. */}
                </TooltipProvider>
                {/* Crown button - only for authenticated non-premium users */}
                {!isPremiumActive && !isAnonymousWithSidebar && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className='p-2 flex justify-center items-center'>
                          <CrownButton />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side='right'
                        align='center'
                        sideOffset={8}
                      >
                        <span>{tMarketing('upgradeToPremium')}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        )}
        <div
          className={cn(
            styles['mainContent'],
            'main-content-area',
            shouldShowSidebar &&
              isSidebarCollapsed &&
              'main-content-area--sidebar-collapsed'
          )}
        >
          <main
            className={cn(
              'flex-grow relative flex flex-col h-full overflow-hidden',
              // Center the loading spinner / anon landing content — but NOT any
              // page that renders its own full-bleed shell (document viewer,
              // /storage, /search, /research, /document-analysis). Centering a
              // flex column shrinks the unconstrained children wrapper to its
              // intrinsic width, squashing that full-bleed content (#5158, #5160).
              // The auth-loading branch respects the same exclusion (#5815): a
              // full-bleed route must never be centered during the auth-loading
              // flash either, so the invariant holds in BOTH states.
              (isLoading || !isAuthenticated) &&
                !rendersOwnFullBleedShell &&
                'items-center justify-center',
              'mobile-view:w-full mobile-view:max-w-full'
            )}
            id='main-content'
          >
            {/* Width-constrained + centered so the banner doesn't span
                behind the chat header's floating Share / Subscribe
                controls on wide viewports. Keeps the same vertical
                rhythm but stops the collision shape. */}
            {/* On compact (mobile/tablet) screens a full-width fixed header
                (FloatingHeaderControls) sits at top:0 / z-1000. Offset the
                banner below it so it isn't hidden behind the bar; on
                large screens the header is floating controls, so revert to
                the normal rhythm (#trial-banner-header-overlap). */}
            <TrialBanner className='mx-auto mt-[76px] compact-header:mt-2 mb-1 w-full max-w-3xl shrink-0 px-3' />
            <div className='flex-grow flex justify-center overflow-hidden'>
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
